import axios from 'axios';
import qs from 'qs';
import { v4 as uuidv4 } from 'uuid';
import { authFirebase } from './firebase.config';

const apiClientDrawerV2 = axios.create({
  // eslint-disable-next-line no-undef
  baseURL: process.env.VITE_DRAWER_API_URL,
  // baseURL: 'http://localhost:8080/api',
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: (params) => {
    return qs.stringify(params, { encode: true });
  },
});

// Request interceptor for authentication and metadata
apiClientDrawerV2.interceptors.request.use(
  async (config) => {
    const user = authFirebase.currentUser;

    if (user) {
      try {
        const idToken = await user.getIdToken();
        config.headers.Authorization = `Bearer ${idToken}`;
      } catch (error) {
        try {
          const refreshedIdToken = await user.getIdToken(true);
          config.headers.Authorization = `Bearer ${refreshedIdToken}`;
        } catch (refreshError) {
          // eslint-disable-next-line no-console
          console.log(refreshError, 'error refreshing Firebase token');
          throw refreshError;
        }
        // eslint-disable-next-line no-console
        console.log(error, 'error getting Firebase token');
        throw error;
      }
    }

    // Add created_by and modified_by for POST/PUT requests
    if (config.method === 'post' || config.method === 'put') {
      // eslint-disable-next-line no-undef
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user.id) {
        config.data = {
          ...config.data,
          created_by: user.id,
          modified_by: user.id,
        };
      }
    }

    // Generate UUID for new records (POST requests)
    if (config.method === 'post' && config.data && !config.data.id) {
      config.data.id = uuidv4();
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClientDrawerV2.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle validation errors (422) and other API-specific errors
    if (error.response?.status === 422) {
      const errorDetail = error.response.data?.detail;
      if (Array.isArray(errorDetail)) {
        const errorMessages = errorDetail.map((err: any) => err.msg).join(', ');
        // eslint-disable-next-line no-console
        console.error('Validation Error:', errorMessages);
      }
    }
    return Promise.reject(error);
  }
);

authFirebase.onAuthStateChanged(async (user) => {
  if (user) {
    const idToken = await user.getIdToken(true);
    apiClientDrawerV2.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;
  }
});

/**
 * Clears the sidebar-v3 cache for a user so permission changes show up immediately
 * instead of after the cache TTL. Call it after every permission-affecting write,
 * in a `finally`, since grant endpoints can fail after the DB write has committed.
 * Never throws and never toasts: a failed invalidation only means a stale sidebar.
 *
 * Defence-in-depth stopgap: User-Service is gaining server-side invalidation;
 * keep this until that has shipped everywhere.
 */
export const invalidateUserSidebarCache = async (userId: number | string): Promise<void> => {
  try {
    await apiClientDrawerV2.post(`/admin/invalidate-user-cache?user_id=${userId}`);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Failed to invalidate sidebar cache for user ${userId}`, error);
  }
};

export default apiClientDrawerV2; 